import {
  type CustomerApplicationFieldValueContract,
  type CustomerApplicationSourceLinkContract,
  type CustomerApplicationSourceSummaryContract,
  type CustomerFieldBindingContract,
  type CustomerFieldMappingTarget,
  type QuestionnaireSourceAnswerDetail,
  type QuestionnaireSourceRecordContract,
  customerFieldTargetOptions,
} from '@/contracts/customer-application-binding'
import type { CustomerFieldMappingContract } from '@/contracts/customers'
import { createSyntheticMeta } from '@/contracts/questionnaire'
import type { CustomerApplication, SalesUser } from '@/lib/business-workflow'
import { formatTaiwanAddressAnswer } from '@/lib/taiwan-address'
import type { Form, Question, ResponseSummary } from '@/types/form'

export const SOURCE_RECORD_ALREADY_LINKED = 'SOURCE_RECORD_ALREADY_LINKED'
export const SOURCE_RECORD_NOT_FOUND = 'SOURCE_RECORD_NOT_FOUND'
export const SOURCE_RECORD_UNAVAILABLE = 'SOURCE_RECORD_UNAVAILABLE'

export class CustomerApplicationBindingError extends Error {
  constructor(
    readonly code:
      | typeof SOURCE_RECORD_ALREADY_LINKED
      | typeof SOURCE_RECORD_NOT_FOUND
      | typeof SOURCE_RECORD_UNAVAILABLE,
    message: string,
    readonly status: 404 | 409 | 422,
  ) {
    super(message)
    this.name = 'CustomerApplicationBindingError'
  }
}

type SourceRecordBuildInput = {
  forms: Form[]
  responses: ResponseSummary[]
  sourceLinks: CustomerApplicationSourceLinkContract[]
  applications: CustomerApplication[]
  salesUsers: SalesUser[]
  formDetails?: Record<string, { questions: Question[] }>
  fieldBindings?: CustomerFieldBindingContract[]
}

type FieldValueBuildInput = {
  record: QuestionnaireSourceRecordContract
  response: ResponseSummary | null
  questions: Question[]
  bindings: CustomerFieldBindingContract[]
}

type ApplicationBuildInput = {
  record: QuestionnaireSourceRecordContract
  fieldValues: CustomerApplicationFieldValueContract[]
  submitter: SalesUser
  supervisor: SalesUser
  applicationId: string
  submittedAt: string
}

type ManualApplicationBuildInput = {
  input: Pick<
    CustomerApplication,
    | 'formId'
    | 'formTitle'
    | 'formDescription'
    | 'companyName'
    | 'taxId'
    | 'industry'
    | 'customerType'
    | 'departmentName'
    | 'companySize'
    | 'website'
    | 'companyAddress'
    | 'companyPhone'
    | 'contactName'
    | 'contactTitle'
    | 'contactEmail'
    | 'contactPhone'
    | 'decisionRole'
    | 'paymentTerms'
    | 'creditLimit'
    | 'salesRegion'
    | 'expectedValue'
    | 'source'
    | 'needSummary'
    | 'internalNotes'
  >
  submitter: SalesUser
  supervisor: SalesUser
  applicationId: string
  submittedAt: string
}

const targetLabelMap = new Map(
  customerFieldTargetOptions.map((option) => [option.value, option.shortLabel]),
)
const targetOptionMap = new Map(customerFieldTargetOptions.map((option) => [option.value, option]))

function normalize(value: unknown) {
  return String(value ?? '').trim()
}

function targetLabel(targetField: CustomerFieldMappingTarget) {
  return targetLabelMap.get(targetField) ?? targetField
}

function targetOption(targetField: CustomerFieldMappingTarget) {
  return targetOptionMap.get(targetField)
}

function targetFullLabel(targetField: CustomerFieldMappingTarget) {
  const option = targetOption(targetField)
  return option?.label ?? targetLabel(targetField)
}

function bindingErpMetadata(binding: CustomerFieldBindingContract) {
  const option = targetOption(binding.targetField)
  return {
    targetLabel: binding.targetFullLabel ?? option?.label ?? binding.targetLabel,
    dbFieldName: binding.dbFieldName ?? option?.dbFieldName,
    dataType: binding.dataType ?? option?.dataType,
  }
}

function fieldValueErpMetadata(targetField: CustomerFieldMappingTarget) {
  const option = targetOption(targetField)
  return {
    targetLabel: option?.label ?? targetLabel(targetField),
    dbFieldName: option?.dbFieldName,
    dataType: option?.dataType,
  }
}

export function createFieldBindingsFromMappings(
  mappings: CustomerFieldMappingContract[],
  updatedAt = new Date().toISOString(),
): CustomerFieldBindingContract[] {
  return mappings.map((mapping) => ({
    id: mapping.id,
    formId: mapping.formId,
    questionId: mapping.questionId,
    questionTitle: mapping.questionTitle,
    targetField: mapping.targetField,
    targetLabel: mapping.targetLabel ?? targetLabel(mapping.targetField),
    targetFullLabel: targetFullLabel(mapping.targetField),
    dbFieldName: mapping.dbFieldName ?? targetOption(mapping.targetField)?.dbFieldName,
    dataType: mapping.dataType ?? targetOption(mapping.targetField)?.dataType,
    sourceType: 'question_answer',
    transform: mapping.transform ?? 'trim',
    requiredForApproval: mapping.requiredForApproval,
    overwritePolicy: mapping.overwritePolicy ?? 'prefill_if_empty',
    confidenceHint: mapping.confidenceHint ?? 'exact',
    updatedAt: mapping.updatedAt ?? updatedAt,
  }))
}

export function listQuestionnaireSourceRecords({
  forms,
  responses,
  sourceLinks,
  applications,
  salesUsers,
  formDetails = {},
  fieldBindings = [],
}: SourceRecordBuildInput): QuestionnaireSourceRecordContract[] {
  const activeLinks = sourceLinks.filter((link) => link.status === 'active')

  return responses.map((response) => {
    const form = forms.find((item) => item.id === response.formId)
    const questions = formDetails[response.formId]?.questions ?? []
    const bindings = fieldBindings.filter((binding) => binding.formId === response.formId)
    const answerDetails = buildSourceAnswerDetails(response, questions, bindings)
    const link = activeLinks.find((item) => item.responseId === response.id)
    const linkedApplication = link
      ? applications.find((application) => application.id === link.applicationId)
      : undefined
    const salesUser = findSalesUserForResponse(response, salesUsers)

    return {
      id: `source-${response.id}`,
      workspaceId: form?.workspaceId ?? 'mock-workspace',
      formId: response.formId,
      formTitle: form?.title ?? '未知問卷',
      responseId: response.id,
      submittedAt: response.submittedAt,
      submittedByName: response.customer?.contactName,
      submittedByEmail: response.customer?.contactEmail,
      salesUserId: salesUser?.id,
      salesUserName: salesUser?.name ?? response.customer?.ownerName,
      firstAnswers: pickImportantAnswerDetails(answerDetails)
        .slice(0, 3)
        .map((answer) => ({
          questionId: answer.questionId,
          questionTitle: answer.questionTitle,
          valuePreview: answer.valuePreview,
        })),
      answerDetails,
      linkStatus: link ? 'linked' : response.status === 'complete' ? 'available' : 'unavailable',
      archiveStatus: response.archiveStatus ?? 'active',
      archivedAt: response.archivedAt,
      linkedApplicationId: linkedApplication?.id,
      linkedApplicationNumber: linkedApplication ? `APP-${linkedApplication.id}` : undefined,
      linkedApplicationStatus: linkedApplication?.status,
      sourceMeta: createSyntheticMeta(),
    }
  })
}

function buildSourceAnswerDetails(
  response: ResponseSummary,
  questions: Question[],
  bindings: CustomerFieldBindingContract[],
): QuestionnaireSourceAnswerDetail[] {
  return Object.entries(response.answers).map(([questionId, value]) => {
    const question = questions.find((item) => item.id === questionId)
    const binding =
      bindings.find((item) => item.questionId === questionId) ??
      bindings.find((item) => item.questionTitle === question?.title)
    const valuePreview = normalizeAnswerValue(value)
    const metadata = binding ? bindingErpMetadata(binding) : null

    return {
      questionId,
      questionTitle: question?.title ?? binding?.questionTitle ?? questionId,
      questionType: question?.type ?? inferQuestionTypeFromBinding(binding),
      valuePreview,
      mappedTarget: binding?.targetField,
      mappedTargetLabel: binding?.targetLabel,
      mappedTargetFullLabel: metadata?.targetLabel,
      mappedDbFieldName: metadata?.dbFieldName,
      mappedDataType: metadata?.dataType,
      sourceState: binding
        ? valuePreview
          ? 'prefilled_from_question'
          : 'missing_source'
        : 'manual',
    }
  })
}

function pickImportantAnswerDetails(details: QuestionnaireSourceAnswerDetail[]) {
  const priority: CustomerFieldMappingTarget[] = [
    'companyName',
    'companyFullName',
    'customerShortName',
    'taxId',
    'contactName',
    'email',
    'contactEmail',
    'phone1',
    'contactPhone',
    'deliveryAddress',
    'submittedBy',
    'needSummary',
    'remark',
  ]
  return [...details].sort((left, right) => {
    const leftIndex = left.mappedTarget ? priority.indexOf(left.mappedTarget) : -1
    const rightIndex = right.mappedTarget ? priority.indexOf(right.mappedTarget) : -1
    if (leftIndex >= 0 && rightIndex >= 0) return leftIndex - rightIndex
    if (leftIndex >= 0) return -1
    if (rightIndex >= 0) return 1
    return 0
  })
}

export function ensureSourceRecordAvailable(
  sourceRecordId: string,
  records: QuestionnaireSourceRecordContract[],
) {
  const record = records.find((item) => item.id === sourceRecordId)
  if (!record) {
    throw new CustomerApplicationBindingError(SOURCE_RECORD_NOT_FOUND, '找不到問卷記錄。', 404)
  }
  if (record.linkStatus === 'linked') {
    throw new CustomerApplicationBindingError(
      SOURCE_RECORD_ALREADY_LINKED,
      '這筆問卷記錄已建立新客申請。',
      409,
    )
  }
  if (record.linkStatus === 'unavailable') {
    throw new CustomerApplicationBindingError(
      SOURCE_RECORD_UNAVAILABLE,
      '這筆問卷記錄尚未完成，不能建立新客申請。',
      422,
    )
  }
  if (record.archiveStatus === 'archived') {
    throw new CustomerApplicationBindingError(
      SOURCE_RECORD_UNAVAILABLE,
      '這筆問卷記錄已封存，不能建立新客申請。',
      422,
    )
  }
  return record
}

export function buildFieldValuesFromSourceRecord({
  record,
  response,
  questions,
  bindings,
}: FieldValueBuildInput): CustomerApplicationFieldValueContract[] {
  return bindings
    .filter((binding) => binding.formId === record.formId)
    .map((binding) => {
      const resolved = resolveBindingValue(binding, response, questions)
      const metadata = bindingErpMetadata(binding)
      return {
        fieldKey: binding.targetField,
        label: binding.targetLabel,
        targetLabel: metadata.targetLabel,
        dbFieldName: metadata.dbFieldName,
        dataType: metadata.dataType,
        value: resolved.value,
        originalValue: resolved.value || undefined,
        sourceState: resolved.value ? 'prefilled_from_question' : 'missing_source',
        sourceQuestionId: binding.questionId,
        sourceQuestionTitle: binding.questionTitle,
        sourceAnswerId: resolved.sourceAnswerId,
      }
    })
}

export function buildManualFieldValues(
  values: Partial<Record<CustomerFieldMappingTarget, string | number | undefined>>,
): CustomerApplicationFieldValueContract[] {
  return customerFieldTargetOptions
    .filter((option) => values[option.value] !== undefined)
    .map((option) => ({
      fieldKey: option.value,
      label: option.shortLabel,
      targetLabel: option.label,
      dbFieldName: option.dbFieldName,
      dataType: option.dataType,
      value: normalize(values[option.value]),
      sourceState: 'manual',
    }))
}

export function overrideApplicationFieldValue(
  values: CustomerApplicationFieldValueContract[],
  fieldKey: CustomerFieldMappingTarget,
  nextValue: string,
): CustomerApplicationFieldValueContract[] {
  const existing = values.find((value) => value.fieldKey === fieldKey)
  if (!existing) {
    return [
      ...values,
      {
        fieldKey,
        label: targetLabel(fieldKey),
        ...fieldValueErpMetadata(fieldKey),
        value: nextValue,
        sourceState: 'manual',
      },
    ]
  }

  return values.map((value) =>
    value.fieldKey === fieldKey
      ? {
          ...value,
          ...fieldValueErpMetadata(fieldKey),
          value: nextValue,
          originalValue: value.originalValue ?? value.value,
          sourceState:
            value.sourceState === 'prefilled_from_question' ||
            value.sourceState === 'missing_source'
              ? 'overridden'
              : value.sourceState,
        }
      : value,
  )
}

export function buildApplicationFromSourceRecord({
  record,
  fieldValues,
  submitter,
  supervisor,
  applicationId,
  submittedAt,
}: ApplicationBuildInput): CustomerApplication {
  const fieldMap = toFieldMap(fieldValues)
  const companyName = firstFieldValue(
    fieldMap,
    'customerShortName',
    'companyName',
    'companyFullName',
  )
  const contactEmail = firstFieldValue(fieldMap, 'contactEmail', 'email', 'billingEmail')
  const contactPhone = firstFieldValue(fieldMap, 'contactPhone', 'phone1', 'phone2')
  const companyAddress = firstFieldValue(
    fieldMap,
    'companyAddress',
    'deliveryAddress',
    'registeredAddress',
    'invoiceAddress',
  )
  const industry = firstFieldValue(
    fieldMap,
    'customerChannel',
    'industry',
  )
  return {
    id: applicationId,
    formId: record.formId,
    formTitle: record.formTitle,
    formDescription: '由問卷記錄自動帶入的新客戶建檔申請。',
    submittedByEmail: submitter.email,
    submittedByName: submitter.name,
    supervisorEmail: supervisor.email,
    supervisorName: supervisor.name,
    teamId: submitter.teamId,
    teamName: submitter.teamName,
    status: 'pending_review',
    companyName,
    taxId: fieldMap.taxId,
    industry,
    customerType: firstFieldValue(fieldMap, 'customerType'),
    departmentName: firstFieldValue(fieldMap, 'department'),
    companySize: firstFieldValue(fieldMap, 'companySize', 'employeeCountRange'),
    website: fieldMap.website,
    companyAddress,
    companyPhone: firstFieldValue(fieldMap, 'companyPhone', 'phone1'),
    contactName: fieldMap.contactName,
    contactTitle: firstFieldValue(fieldMap, 'contactTitle'),
    contactEmail,
    contactPhone,
    decisionRole: fieldMap.decisionRole,
    paymentTerms: firstFieldValue(fieldMap, 'paymentTerms'),
    creditLimit: toNumber(fieldMap.creditLimit),
    salesRegion: firstFieldValue(fieldMap, 'salesRegion', 'region') || submitter.territory,
    expectedValue: toNumber(fieldMap.expectedValue),
    source: '問卷記錄',
    needSummary:
      firstFieldValue(fieldMap, 'needSummary', 'remark') ||
      '由問卷記錄自動建立，請審核欄位來源與重複客戶提示。',
    internalNotes:
      firstFieldValue(fieldMap, 'internalNotes', 'remark') ||
      '由問卷記錄自動建立，請審核欄位來源與重複客戶提示。',
    submittedAt,
    reviewedAt: '',
    reviewerEmail: '',
    reviewerName: '',
    reviewNotes: '',
    answers: fieldValues.map((fieldValue) => ({
      id: fieldValue.sourceAnswerId ?? `field-${fieldValue.fieldKey}`,
      questionTitle: fieldValue.sourceQuestionTitle ?? fieldValue.label,
      questionType: 'BOUND_FIELD',
      answer: fieldValue.value,
      mappedField: fieldValue.fieldKey,
    })),
  }
}

export function buildManualApplication({
  input,
  submitter,
  supervisor,
  applicationId,
  submittedAt,
}: ManualApplicationBuildInput): CustomerApplication {
  return {
    ...input,
    id: applicationId,
    submittedByEmail: submitter.email,
    submittedByName: submitter.name,
    supervisorEmail: supervisor.email,
    supervisorName: supervisor.name,
    teamId: submitter.teamId,
    teamName: submitter.teamName,
    status: 'pending_review',
    submittedAt,
    reviewedAt: '',
    reviewerEmail: '',
    reviewerName: '',
    reviewNotes: '',
    answers: buildManualFieldValues(input).map((fieldValue) => ({
      id: `manual-${fieldValue.fieldKey}`,
      questionTitle: fieldValue.label,
      questionType: 'MANUAL_FIELD',
      answer: fieldValue.value,
      mappedField: fieldValue.fieldKey,
    })),
  }
}

export function buildSourceSummary(
  application: CustomerApplication,
  sourceLinks: CustomerApplicationSourceLinkContract[],
  records: QuestionnaireSourceRecordContract[],
): CustomerApplicationSourceSummaryContract {
  const link = sourceLinks.find(
    (item) => item.applicationId === application.id && item.status === 'active',
  )
  if (!link) {
    return {
      sourceType: 'manual',
      createdByName: application.submittedByName,
    }
  }
  const record = records.find((item) => item.id === link.sourceRecordId)
  return {
    sourceType: 'questionnaire_record',
    sourceRecordId: link.sourceRecordId,
    responseId: link.responseId,
    formId: link.formId,
    formTitle: record?.formTitle ?? application.formTitle,
    submittedAt: record?.submittedAt ?? application.submittedAt,
    createdByName: application.submittedByName,
    linkStatus: 'linked',
    archiveStatus: record?.archiveStatus ?? 'active',
    archivedAt: record?.archivedAt,
  }
}

function findSalesUserForResponse(response: ResponseSummary, salesUsers: SalesUser[]) {
  if (!response.customer?.ownerName) return undefined
  return salesUsers.find((user) => user.name === response.customer?.ownerName)
}

function resolveBindingValue(
  binding: CustomerFieldBindingContract,
  response: ResponseSummary | null,
  questions: Question[],
) {
  if (!response) return { value: '', sourceAnswerId: undefined }

  const directValue = response.answers[binding.questionId]
  if (directValue !== undefined && directValue !== null && directValue !== '') {
    return {
      value: applyTransform(directValue, binding.transform),
      sourceAnswerId: `${response.id}:${binding.questionId}`,
    }
  }

  const matchedQuestion = questions.find((question) => question.title === binding.questionTitle)
  if (matchedQuestion) {
    const titleValue = response.answers[matchedQuestion.id]
    if (titleValue !== undefined && titleValue !== null && titleValue !== '') {
      return {
        value: applyTransform(titleValue, binding.transform),
        sourceAnswerId: `${response.id}:${matchedQuestion.id}`,
      }
    }
  }

  const customerValue = getCustomerValue(response, binding.targetField)
  if (customerValue) {
    return {
      value: applyTransform(customerValue, binding.transform),
      sourceAnswerId: `${response.id}:customer.${binding.targetField}`,
    }
  }

  return { value: '', sourceAnswerId: undefined }
}

function normalizeAnswerValue(value: unknown) {
  if (Array.isArray(value))
    return value
      .map((item) => normalize(item))
      .filter(Boolean)
      .join('、')
  if (value && typeof value === 'object') {
    const address = formatTaiwanAddressAnswer(value)
    if (address) return address
    return Object.entries(value as Record<string, unknown>)
      .map(([key, item]) => `${key}: ${normalize(item)}`)
      .join('；')
  }
  return normalize(value)
}

function inferQuestionTypeFromBinding(binding: CustomerFieldBindingContract | undefined) {
  if (!binding) return 'UNKNOWN'
  if (binding.transform === 'email') return 'EMAIL'
  if (binding.transform === 'phone') return 'PHONE_NUMBER'
  if (binding.transform === 'url') return 'WEBSITE'
  if (binding.transform === 'number' || binding.transform === 'currency') return 'NUMBER'
  return 'SHORT_TEXT'
}

function getCustomerValue(response: ResponseSummary, field: CustomerFieldMappingTarget) {
  switch (field) {
    case 'companyName':
    case 'companyFullName':
    case 'customerShortName':
      return response.customer?.companyName
    case 'contactName':
      return response.customer?.contactName
    case 'contactTitle':
    case 'ownerName':
      return response.customer?.contactTitle
    case 'contactEmail':
    case 'email':
    case 'billingEmail':
      return response.customer?.contactEmail
    case 'contactPhone':
    case 'phone1':
    case 'phone2':
      return response.customer?.contactPhone
    case 'ownerEmail':
    case 'submittedBy':
      return response.customer?.ownerName
    default:
      return undefined
  }
}

function applyTransform(value: unknown, transform: CustomerFieldBindingContract['transform']) {
  const text = normalize(value)
  if (!text) return ''
  if (transform === 'number' || transform === 'currency') {
    return text.replace(/[^\d.-]/g, '')
  }
  if (transform === 'email') return text.toLocaleLowerCase()
  return text
}

function toFieldMap(values: CustomerApplicationFieldValueContract[]) {
  return Object.fromEntries(
    customerFieldTargetOptions.map((option) => [
      option.value,
      values.find((value) => value.fieldKey === option.value)?.value ?? '',
    ]),
  ) as Record<CustomerFieldMappingTarget, string>
}

function firstFieldValue(
  fieldMap: Record<CustomerFieldMappingTarget, string>,
  ...fields: CustomerFieldMappingTarget[]
) {
  return fields.map((field) => fieldMap[field]).find((value) => value.trim()) ?? ''
}

function toNumber(value: string) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}
